// @ts-nocheck

import { useState, useRef, useEffect } from "react";

const API_URL = "/api-ia/api/chat";
const STORAGE_KEY = "legalbot_messages_v1";
const MAX_HISTORY = 6;

const CANAL_LABELS = {
  acuerdos: "Acuerdos de Sala Plena",
  resoluciones: "Resoluciones del Tribunal",
  suma: "SUMA · Doctrina",
  general: "Conversación general",
};

const WELCOME = {
  role: "assistant",
  content:
    "¡Hola! Soy LegalBot, tu asistente en contrataciones del Estado peruano. Hazme una pregunta!",
};

const styles = {
  app: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    background: "#f5f5f5",
    fontFamily: "Arial, sans-serif",
  },

  header: {
    background: "#202123",
    color: "white",
    padding: "14px 18px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  logo: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },

  logoCircle: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    background: "#4f46e5",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "bold",
  },

  brand: {
    fontSize: "15px",
    fontWeight: 700,
  },

  tagline: {
    fontSize: "12px",
    opacity: 0.8,
  },

  clearBtn: {
    border: "1px solid rgba(255,255,255,0.2)",
    background: "transparent",
    color: "white",
    padding: "8px 12px",
    borderRadius: "10px",
    cursor: "pointer",
  },

  chat: {
    flex: 1,
    overflowY: "auto",
    padding: "20px",
  },

  msg: {
    display: "flex",
    marginBottom: "18px",
    gap: "10px",
  },

  avatar: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    background: "#111827",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    fontWeight: "bold",
    flexShrink: 0,
  },

  bubbleUser: {
    background: "#2563eb",
    color: "white",
    padding: "14px",
    borderRadius: "14px",
    maxWidth: "80%",
    whiteSpace: "pre-wrap",
  },

  bubbleIA: {
    background: "white",
    color: "#111827",
    padding: "14px",
    borderRadius: "14px",
    maxWidth: "80%",
    border: "1px solid #e5e7eb",
    whiteSpace: "pre-wrap",
  },

  meta: {
    marginBottom: "8px",
    fontSize: "12px",
    opacity: 0.7,
  },

  composer: {
    background: "white",
    borderTop: "1px solid #ddd",
    padding: "16px",
  },

  textarea: {
    width: "100%",
    minHeight: "55px",
    maxHeight: "200px",
    padding: "14px",
    borderRadius: "12px",
    border: "1px solid #ccc",
    resize: "none",
    outline: "none",
    fontSize: "14px",
    boxSizing: "border-box",
  },

  sendBtn: {
    marginTop: "10px",
    background: "#2563eb",
    color: "white",
    border: "none",
    borderRadius: "10px",
    padding: "10px 16px",
    cursor: "pointer",
    fontWeight: 600,
  },

  hint: {
    marginTop: "10px",
    fontSize: "12px",
    color: "#666",
  },

  error: {
    marginTop: "10px",
    color: "#dc2626",
    fontSize: "13px",
  },
};

function loadMessages() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);

    if (raw) {
      const parsed = JSON.parse(raw);

      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch {}

  return [WELCOME];
}

function Message({
  role,
  content,
  canal,
  tiempo,
  sources,
}) {
  return (
    <div style={styles.msg}>
      <div style={styles.avatar}>
        {role === "user" ? "TÚ" : "IA"}
      </div>

      <div
        style={
          role === "user"
            ? styles.bubbleUser
            : styles.bubbleIA
        }
      >
        {canal && (
          <div style={styles.meta}>
            {CANAL_LABELS[canal] || canal}

            {tiempo != null && ` · ${tiempo}s`}
          </div>
        )}

        <div>{content}</div>

        {sources && sources.length > 0 && (
          <details style={{ marginTop: "10px" }}>
            <summary>
              Fuentes ({sources.length})
            </summary>

            <ul>
              {sources.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div style={styles.msg}>
      <div style={styles.avatar}>IA</div>

      <div style={styles.bubbleIA}>
        Escribiendo...
      </div>
    </div>
  );
}

export default function LegalBot() {
  const [messages, setMessages] = useState(loadMessages);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const listRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(messages)
    );
  }, [messages]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop =
        listRef.current.scrollHeight;
    }
  }, [messages, loading]);

  async function sendMessage(e) {
    e?.preventDefault();

    const pregunta = input.trim();

    if (!pregunta || loading) return;

    setError(null);

    const history = messages
      .filter(
        (m) =>
          !m.isError &&
          (m.role === "user" ||
            m.role === "assistant")
      )
      .slice(-MAX_HISTORY)
      .map((m) => ({
        role: m.role,
        content: m.content,
      }));

    setMessages((prev) => [
      ...prev,
      {
        role: "user",
        content: pregunta,
      },
    ]);

    setInput("");
    setLoading(true);

    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: pregunta,
          history,
        }),
      });

      if (!res.ok) {
        throw new Error(
          `Error ${res.status}`
        );
      }

      const data = await res.json();

      const respuesta =
        data.response ||
        data.respuesta ||
        data.answer ||
        data.message ||
        "Sin respuesta";

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: respuesta,
          canal: data.canal,
          tiempo: data.tiempo_segundos,
          sources: data.sources || [],
        },
      ]);
    } catch (err) {
      console.error(err);

      setError(
        "Hubo un problema al contactar al servidor."
      );

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "⚠️ No pude obtener una respuesta. Revisa tu backend e inténtalo nuevamente.",
          isError: true,
        },
      ]);
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  function clearChat() {
    setMessages([WELCOME]);

    localStorage.removeItem(STORAGE_KEY);
  }

  return (
    <div style={styles.app}>
      <header style={styles.header}>
        <div style={styles.logo}>
          <div style={styles.logoCircle}>
            L
          </div>

          <div>
            <div style={styles.brand}>
              LegalBot CP
            </div>

            <div style={styles.tagline}>
              Contrataciones del Estado · Perú
            </div>
          </div>
        </div>

        <button
          style={styles.clearBtn}
          onClick={clearChat}
        >
          Nueva
        </button>
      </header>

      <main style={styles.chat} ref={listRef}>
        {messages.map((m, i) => (
          <Message key={i} {...m} />
        ))}

        {loading && <TypingIndicator />}
      </main>

      <footer style={styles.composer}>
        <form onSubmit={sendMessage}>
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) =>
              setInput(e.target.value)
            }
            onKeyDown={handleKeyDown}
            placeholder="Escribe tu consulta legal..."
            style={styles.textarea}
            disabled={loading}
          />

          <button
            type="submit"
            style={styles.sendBtn}
            disabled={
              !input.trim() || loading
            }
          >
            Enviar
          </button>
        </form>

        <div style={styles.hint}>
          Enter para enviar · Shift + Enter
          para nueva línea
        </div>

        {error && (
          <div style={styles.error}>
            {error}
          </div>
        )}
      </footer>
    </div>
  );
}